import React from "react";
import { Input } from "reactstrap";
import moment from "moment";
import { useTheme } from "../../../../theme/ThemeProvider";

interface ControlDatePickerProps {
    inputProps: Record<string, any>;
    inputStyle: React.CSSProperties;
    placeholder: string;
    name?: string;
    value?: string;
    min?: string;
    invalid?: boolean;
    className: string;
    onDateChange: (value: string) => void;
}

export const ControlDatePicker: React.FC<ControlDatePickerProps> = ({
    inputProps,
    inputStyle,
    placeholder,
    name,
    value,
    min,
    invalid,
    className,
    onDateChange,
}) => {
    const theme = useTheme();
    const [isPickerOpen, setIsPickerOpen] = React.useState(false);
    const [viewMonth, setViewMonth] = React.useState(moment());
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (value) {
            setViewMonth(moment(value, "YYYY-MM-DD"));
        }
    }, [value]);

    React.useEffect(() => {
        const handlePointerDown = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsPickerOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, []);

    const selectedMoment = value ? moment(value, "YYYY-MM-DD") : undefined;
    const minMoment = min ? moment(min, "YYYY-MM-DD") : undefined;
    const calendarStart = viewMonth.clone().startOf("month").startOf("week");
    const calendarDays = Array.from({ length: 42 }, (_, index) => calendarStart.clone().add(index, "day"));
    const displayValue = value ? moment(value, "YYYY-MM-DD").format("MM/DD/YYYY") : "";
    const monthOptions = moment.months();
    const currentYear = moment().year();
    const selectedYear = selectedMoment?.year();
    const minYear = minMoment?.year();
    const firstYear = Math.min(minYear ?? currentYear - 25, selectedYear ?? currentYear - 25, currentYear - 25);
    const lastYear = Math.max(selectedYear ?? currentYear + 10, currentYear + 10);
    const yearOptions = Array.from({ length: lastYear - firstYear + 1 }, (_, index) => firstYear + index);

    const styles = {
        wrapper: {
            position: "relative",
        } as React.CSSProperties,
        iconButton: {
            alignItems: "center",
            background: "transparent",
            border: 0,
            bottom: 0,
            color: theme.palette.accent.dark,
            display: "flex",
            justifyContent: "center",
            position: "absolute",
            right: 0,
            top: 0,
            width: "42px",
        } as React.CSSProperties,
        popover: {
            background: theme.palette.background.main,
            border: `1px solid ${theme.palette.accent.dark}`,
            borderRadius: theme.spacePx.sp02,
            boxShadow: theme.shadowTypes.popover,
            marginTop: theme.spacePx.sp01,
            padding: theme.spacePx.sp03,
            position: "absolute",
            width: "294px",
            zIndex: 20,
        } as React.CSSProperties,
        header: {
            alignItems: "center",
            background: theme.palette.accent.dark,
            borderRadius: theme.spacePx.sp01,
            color: theme.palette.white,
            display: "flex",
            justifyContent: "space-between",
            marginBottom: theme.spacePx.sp02,
            padding: `${theme.spacePx.sp02} ${theme.spacePx.sp03}`,
        } as React.CSSProperties,
        navButton: {
            background: "transparent",
            border: 0,
            color: theme.palette.white,
            fontSize: theme.font.sizes.lg,
            lineHeight: 1,
        } as React.CSSProperties,
        headerSelectGroup: {
            display: "flex",
            gap: theme.spacePx.sp01,
        } as React.CSSProperties,
        headerSelect: {
            background: theme.palette.background.main,
            border: 0,
            borderRadius: theme.spacePx.sp01,
            color: theme.palette.accent.dark,
            fontSize: theme.font.sizes.sm,
            fontWeight: theme.font.weights.semiBold,
            maxWidth: "118px",
            padding: `${theme.spacePx.sp01} ${theme.spacePx.sp02}`,
        } as React.CSSProperties,
        yearSelect: {
            background: theme.palette.background.main,
            border: 0,
            borderRadius: theme.spacePx.sp01,
            color: theme.palette.accent.dark,
            fontSize: theme.font.sizes.sm,
            fontWeight: theme.font.weights.semiBold,
            padding: `${theme.spacePx.sp01} ${theme.spacePx.sp02}`,
            width: "78px",
        } as React.CSSProperties,
        weekHeader: {
            color: theme.palette.text.dark,
            display: "grid",
            fontSize: theme.font.sizes.sm,
            fontWeight: theme.font.weights.bold,
            gridTemplateColumns: "repeat(7, 1fr)",
            marginBottom: theme.spacePx.sp01,
            textAlign: "center",
        } as React.CSSProperties,
        grid: {
            display: "grid",
            gap: theme.spacePx.sp01,
            gridTemplateColumns: "repeat(7, 1fr)",
        } as React.CSSProperties,
        footer: {
            borderTop: `1px solid ${theme.palette.divider.light}`,
            display: "flex",
            justifyContent: "space-between",
            marginTop: theme.spacePx.sp03,
            paddingTop: theme.spacePx.sp02,
        } as React.CSSProperties,
        footerButton: {
            background: "transparent",
            border: 0,
            color: theme.palette.link.main,
            fontWeight: theme.font.weights.semiBold,
        } as React.CSSProperties,
    };

    const selectDate = (date: string) => {
        onDateChange(date);
        setIsPickerOpen(false);
    };

    return (
        <div ref={wrapperRef} style={styles.wrapper}>
            <Input
                type="text"
                {...inputProps}
                autoComplete="off"
                readOnly
                style={{ ...inputStyle, paddingRight: "48px" }}
                placeholder={placeholder}
                id={name}
                name={name}
                value={displayValue}
                onClick={() => setIsPickerOpen(true)}
                onFocus={() => setIsPickerOpen(true)}
                invalid={invalid}
                className={className}
            />
            <button
                type="button"
                aria-label={`Open ${placeholder || name || "date"} picker`}
                style={styles.iconButton}
                onClick={() => setIsPickerOpen((open) => !open)}
            >
                <i className="bi bi-calendar-event" />
            </button>
            {isPickerOpen && (
                <div style={styles.popover}>
                    <div style={styles.header}>
                        <button
                            type="button"
                            style={styles.navButton}
                            aria-label="Previous month"
                            onClick={() => setViewMonth((month) => month.clone().subtract(1, "month"))}
                        >
                            <i className="bi bi-chevron-left" />
                        </button>
                        <div style={styles.headerSelectGroup}>
                            <select
                                aria-label="Select month"
                                style={styles.headerSelect}
                                value={viewMonth.month()}
                                onChange={(event) => setViewMonth((current) => current.clone().month(Number(event.target.value)))}
                            >
                                {monthOptions.map((month, index) => (
                                    <option key={month} value={index}>{month}</option>
                                ))}
                            </select>
                            <select
                                aria-label="Select year"
                                style={styles.yearSelect}
                                value={viewMonth.year()}
                                onChange={(event) => setViewMonth((current) => current.clone().year(Number(event.target.value)))}
                            >
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            style={styles.navButton}
                            aria-label="Next month"
                            onClick={() => setViewMonth((month) => month.clone().add(1, "month"))}
                        >
                            <i className="bi bi-chevron-right" />
                        </button>
                    </div>
                    <div style={styles.weekHeader}>
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                            <span key={day}>{day}</span>
                        ))}
                    </div>
                    <div style={styles.grid}>
                        {calendarDays.map((day) => {
                            const date = day.format("YYYY-MM-DD");
                            const isOutsideMonth = !day.isSame(viewMonth, "month");
                            const isSelected = selectedMoment?.isSame(day, "day") === true;
                            const isToday = day.isSame(moment(), "day");
                            const isDisabled = minMoment ? day.isBefore(minMoment, "day") : false;
                            const dayStyle: React.CSSProperties = {
                                background: isSelected
                                    ? theme.palette.accent.dark
                                    : isToday
                                        ? theme.palette.accent.light
                                        : theme.palette.background.main,
                                border: `1px solid ${isToday || isSelected ? theme.palette.accent.dark : theme.palette.divider.light}`,
                                borderRadius: theme.spacePx.sp01,
                                color: isSelected || isToday
                                    ? theme.palette.white
                                    : isOutsideMonth
                                        ? theme.palette.disabled.dark
                                        : theme.palette.text.dark,
                                cursor: isDisabled ? "not-allowed" : "pointer",
                                minHeight: "34px",
                                opacity: isDisabled ? 0.45 : 1,
                            };

                            return (
                                <button
                                    key={date}
                                    type="button"
                                    disabled={isDisabled}
                                    style={dayStyle}
                                    onClick={() => selectDate(date)}
                                >
                                    {day.date()}
                                </button>
                            );
                        })}
                    </div>
                    <div style={styles.footer}>
                        <button type="button" style={styles.footerButton} onClick={() => selectDate("")}>
                            Clear
                        </button>
                        <button type="button" style={styles.footerButton} onClick={() => selectDate(moment().format("YYYY-MM-DD"))}>
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};